<template>
  <div class="packing-screen" dir="rtl">
    <header class="topbar">
      <strong>بسته‌بندی سفارش‌های آنلاین</strong>
      <div><button @click="$router.push({ name: 'Home' })">داشبورد</button><button @click="connect">{{ connected ? 'چاپگر وصل است' : 'اتصال چاپگر BLE' }}</button></div>
    </header>
    <details class="printer-settings"><summary>تنظیم اتصال چاپگر</summary><label>Service UUID <input v-model="serviceUuid" dir="ltr"></label><label>Write UUID <input v-model="writeUuid" dir="ltr"></label><small>چاپگر باید BLE و فرمان تصویر ESC/POS را پشتیبانی کند.</small></details>
    <p v-if="message" class="notice" :class="error ? 'bad' : 'good'">{{ message }}</p>

    <section v-if="!session" class="card">
      <h3>۱. سفارش تکی یا چند سفارش</h3>
      <input v-model="orderSearch" placeholder="جست‌وجوی کد سفارش" @keyup.enter="loadOrders">
      <button @click="loadOrders">به‌روزرسانی سفارش‌ها</button>
      <div v-for="order in orders" :key="order.id" class="order-row">
        <label><input type="checkbox" :value="order.id" v-model="selected"> {{ order.code }} <small>({{ order.items }} عدد)</small></label>
      </div>
      <button class="primary" :disabled="!selected.length || busy" @click="prepare">پیشنهاد کارتن برای {{ selected.length }} سفارش</button>
      <div v-if="recent.length" class="recent">
        <h4>ادامهٔ جلسه قبلی</h4>
        <button v-for="entry in recent" :key="entry.id" @click="openSession(entry.id)">جلسه {{ entry.id }} {{ entry.finished ? '(پایان‌یافته)' : '' }}</button>
      </div>
    </section>

    <section v-if="plans.length && !session" class="card">
      <h3>۲. سایز و تعداد کارتن‌ها</h3>
      <div v-for="plan in plans" :key="plan.order_id" class="plan">
        <strong>سفارش {{ orderCode(plan.order_id) }}</strong>
        <p v-if="plan.manual_required" class="warn">ابعاد/وزن کامل نیست یا محصول در سایز ۵ جا نمی‌شود. سایز و تعداد را دستی تعیین کنید.</p>
        <p v-for="id in plan.oversize_products" :key="id" class="warn">محصول {{ id }} بزرگ‌تر از ظرفیت ثبت‌شده است؛ پیش از ادامه، روش بسته‌بندی آن را بررسی کنید.</p>
        <label>تعداد کارتن
          <input type="number" min="1" max="30" v-model.number="plan.manualCount" @change="distribute(plan)">
        </label>
        <div v-for="(carton, index) in plan.cartons" :key="index" class="carton-plan">
          <span>کارتن {{ index + 1 }} — {{ carton.items.reduce((n, row) => n + row.quantity, 0) }} عدد</span>
          <select v-model.number="carton.size"><option v-for="size in sizes" :key="size.number" :value="size.number">سایز {{ size.number }}</option></select>
        </div>
      </div>
      <button class="primary" :disabled="busy || !sizes.length" @click="start">شروع بسته‌بندی</button>
    </section>

    <template v-if="session">
      <section class="card summary">
        <div><strong>جلسه {{ session.id }}</strong><span>{{ session.complete_count }} کامل / {{ session.incomplete_count }} ناقص</span></div>
        <button @click="reload">به‌روزرسانی</button>
        <button v-if="!session.finished" class="primary" :disabled="busy" @click="finish">پایان بسته‌بندی</button>
        <button @click="leave">بازگشت به لیست</button>
      </section>

      <section class="card">
        <h3>کارتن‌ها و برچسب‌ها</h3>
        <article v-for="carton in session.cartons" :key="carton.id" class="carton" :class="carton.complete ? 'ready' : ''">
          <div class="carton-title">{{ carton.order_code }} / کارتن {{ carton.number }} <small>سایز {{ carton.size }}</small></div>
          <div>{{ carton.lines.reduce((n, line) => n + line.confirmed, 0) }} تأیید شده · {{ carton.pending }} باقی · {{ carton.shortage }} کسری</div>
          <div class="actions">
            <button @click="print(carton, 'token')">چاپ شناسه</button>
            <button v-if="carton.address_ready" @click="print(carton, 'address')">{{ carton.address_printed ? 'چاپ مجدد آدرس' : 'چاپ آدرس' }}</button>
            <select v-if="!carton.shipped" :value="carton.size" @change="changeSize(carton, $event.target.value)">
              <option v-for="size in sizes" :key="size.number" :value="size.number">سایز {{ size.number }}</option>
            </select>
            <button v-if="!carton.shipped && !session.finished" @click="splitTarget = carton">افزودن کارتن</button>
          </div>
          <div v-if="awaitingPrint === carton.id" class="print-confirm">
            برچسب آدرس روی کارتن چاپ شد؟
            <button class="primary" @click="confirmPrint(carton)">بله، چاپ شد</button>
            <button @click="awaitingPrint = null">خیر، تلاش مجدد</button>
          </div>
          <p v-if="carton.complete && !carton.address_printed" class="warn">نیازمند چاپ آدرس</p>
        </article>
      </section>

      <section class="card">
        <h3>۳. جمع‌آوری به ترتیب ستون و ردیف</h3>
        <div v-for="(entry, index) in pickRows" :key="entry.line.id" class="pick-row" :class="entry.line.confirmed + entry.line.shortage === entry.line.quantity ? 'done' : ''">
          <div class="pick-position">{{ entry.line.column || '؟' }} / {{ entry.line.row || '؟' }}</div>
          <img v-if="entry.line.picture" :src="imageUrl(entry.line.picture)" alt="" class="product-image">
          <div class="pick-info">
            <b>{{ entry.line.gtin || 'بدون بارکد' }}</b>
            <div>{{ entry.line.name }}</div>
            <small>سفارش {{ entry.carton.order_code }} · کارتن {{ entry.carton.number }}</small>
            <small>{{ entry.line.confirmed }}/{{ entry.line.quantity }} تأیید · {{ entry.line.shortage }} کسری</small>
          </div>
          <div class="pick-actions" v-if="!session.finished && !entry.carton.shipped">
            <button v-if="!entry.line.column || !entry.line.row" @click="setLocation(entry)">ثبت موقعیت</button>
            <button :disabled="entry.line.confirmed + entry.line.shortage >= entry.line.quantity" @click="scanTarget = entry; camera = true">اسکن</button>
            <button :disabled="entry.line.confirmed + entry.line.shortage >= entry.line.quantity" @click="manual(entry)">ثبت دستی</button>
            <button :disabled="entry.line.confirmed + entry.line.shortage >= entry.line.quantity" @click="shortage(entry)">کسری</button>
          </div>
          <div v-if="index === 0 || pickRows[index - 1].line.column !== entry.line.column" class="sr-only">ستون جدید</div>
        </div>
      </section>

      <section v-if="incomplete.length" class="card shortages">
        <h3>بسته‌های ناقص</h3>
        <p v-for="carton in incomplete" :key="carton.id">{{ carton.order_code }} / کارتن {{ carton.number }} — {{ carton.pending }} باقی، {{ carton.shortage }} کسری</p>
      </section>
    </template>

    <div v-if="camera" class="scanner-dialog">
      <button @click="camera = false">بستن دوربین</button>
      <BarcodeScanner :active="camera" :formats="['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39']" @detected="onDetected" />
      <input v-model="scanCode" @keyup.enter="onDetected(scanCode)" placeholder="یا کد را وارد کنید">
    </div>

    <div v-if="splitTarget" class="modal">
      <div class="card">
        <h3>کارتن جدید برای سفارش {{ splitTarget.order_code }}</h3>
        <label>سایز <select v-model.number="splitSize"><option v-for="size in sizes" :key="size.number" :value="size.number">{{ size.number }}</option></select></label>
        <div v-for="line in splitTarget.lines" :key="line.id" v-if="line.quantity > line.confirmed + line.shortage">
          {{ line.name }} — قابل انتقال: {{ line.quantity - line.confirmed - line.shortage }}
          <input type="number" min="0" :max="line.quantity - line.confirmed - line.shortage" v-model.number="splitAmounts[line.id]">
        </div>
        <button class="primary" @click="split">ثبت کارتن جدید</button><button @click="splitTarget = null">انصراف</button>
      </div>
    </div>
  </div>
</template>

<script>
import BarcodeScanner from '@/modules/packing_entry/BarcodeScanner.vue';
import { packingGet, packingPost, packingError } from './api';
import { connectPrinter, printerConnected, printCarton, printerProfile } from './blePrinter';

export default {
  name: 'PackerWorkspace', components: { BarcodeScanner },
  data: () => ({ orders: [], selected: [], orderSearch: '', sizes: [], plans: [], recent: [], session: null,
    message: '', error: false, busy: false, connected: false, camera: false, scanTarget: null,
    scanCode: '', awaitingPrint: null, splitTarget: null, splitSize: 1, splitAmounts: {},
    serviceUuid: '', writeUuid: '' }),
  computed: {
    pickRows() {
      if (!this.session) return [];
      const entries = [];
      this.session.cartons.forEach(carton => carton.lines.forEach(line => entries.push({ carton, line })));
      const location = value => /^\d+$/.test(value || '') ? [0, Number(value)] : [1, value || '~~~~'];
      return entries.sort((a, b) => {
        for (const field of ['column', 'row']) {
          const x = location(a.line[field]), y = location(b.line[field]);
          if (x[0] !== y[0]) return x[0] - y[0];
          if (x[1] < y[1]) return -1;
          if (x[1] > y[1]) return 1;
        }
        return a.carton.order_code.localeCompare(b.carton.order_code);
      });
    },
    incomplete() { return this.session ? this.session.cartons.filter(c => !c.complete) : []; },
  },
  async mounted() {
    if (!this.$store.state.user || this.$store.state.user.user_type !== 'paad') return this.$router.replace('/');
    const profile = printerProfile(); this.serviceUuid = profile.service || ''; this.writeUuid = profile.writer || '';
    await this.loadOrders();
    try { this.sizes = await packingGet('sizes'); this.recent = await packingGet('sessions'); } catch (e) { this.fail(e); }
    const saved = localStorage.getItem('onlinePackingSession');
    if (saved) await this.openSession(saved);
  },
  methods: {
    imageUrl(path) { return /^https?:/.test(path) ? path : `${this.getServerUrl().replace(/\/$/, '')}/${path.replace(/^\//, '')}`; },
    inform(text) { this.message = text; this.error = false; },
    fail(error) { this.message = packingError(error); this.error = true; },
    orderCode(id) { const order = this.orders.find(o => o.id === id); return order ? order.code : id; },
    async loadOrders() { try { this.orders = await packingGet('orders', { q: this.orderSearch }); } catch (e) { this.fail(e); } },
    async connect() { try { const name = await connectPrinter(this.serviceUuid, this.writeUuid); this.connected = printerConnected(); this.inform(`چاپگر ${name} وصل شد.`); } catch (e) { this.fail(e); } },
    async prepare() {
      this.busy = true;
      try {
        this.plans = await Promise.all(this.selected.map(id => packingGet('suggest', { order_id: id })));
        this.plans.forEach(plan => { plan.manualCount = plan.cartons.length || 1; if (!plan.cartons.length) this.distribute(plan); });
      } catch (e) { this.fail(e); } finally { this.busy = false; }
    },
    distribute(plan) {
      const count = Math.max(1, Math.min(30, Number(plan.manualCount) || 1));
      const size = plan.cartons[0] ? plan.cartons[0].size : (this.sizes[0] && this.sizes[0].number);
      const cartons = Array.from({ length: count }, () => ({ size, items: [] }));
      let cursor = 0;
      plan.items.forEach(item => {
        for (let n = 0; n < item.quantity; n += 1) {
          const carton = cartons[cursor % count];
          let row = carton.items.find(r => r.order_item_id === item.order_item_id && r.product_id === item.product_id);
          if (!row) { row = { order_item_id: item.order_item_id, product_id: item.product_id, quantity: 0 }; carton.items.push(row); }
          row.quantity += 1; cursor += 1;
        }
      });
      plan.cartons = cartons.filter(c => c.items.length);
    },
    async start() {
      this.busy = true;
      try {
        this.session = await packingPost('sessions', { plans: this.plans.map(p => ({ order_id: p.order_id, cartons: p.cartons })) });
        localStorage.setItem('onlinePackingSession', this.session.id);
        this.plans = []; this.inform('جلسه ساخته شد. برچسب شناسه هر کارتن را چاپ کنید.');
        if (printerConnected()) {
          for (const carton of this.session.cartons) await printCarton(carton, 'token');
          this.inform('شناسهٔ همهٔ کارتن‌ها به چاپگر ارسال شد. برچسب‌ها را بررسی کنید.');
        }
      } catch (e) { this.fail(e); } finally { this.busy = false; }
    },
    async openSession(id) { try { this.session = await packingGet('session', { id }); localStorage.setItem('onlinePackingSession', id); } catch (e) { localStorage.removeItem('onlinePackingSession'); this.fail(e); } },
    async reload() { if (this.session) await this.openSession(this.session.id); },
    leave() { this.session = null; localStorage.removeItem('onlinePackingSession'); this.loadOrders(); },
    async finish() { try { this.session = await packingPost('finish', { session_id: this.session.id }); this.inform('جلسه پایان یافت؛ بسته‌های ناقص در صف انباردار هستند.'); } catch (e) { this.fail(e); } },
    async print(carton, kind) {
      try {
        await printCarton(carton, kind);
        if (kind === 'address') this.awaitingPrint = carton.id;
        else this.inform(`شناسه کارتن ${carton.number} به چاپگر ارسال شد.`);
      } catch (e) { this.fail(e); }
    },
    async confirmPrint(carton) { try { await packingPost('address-printed', { carton_id: carton.id }); this.awaitingPrint = null; await this.reload(); } catch (e) { this.fail(e); } },
    async changeSize(carton, size) {
      try { await packingPost('size', { carton_id: carton.id, size: Number(size) }); await this.reload(); this.inform('توکن قبلی باطل شد؛ شناسه جدید را چاپ کنید.'); } catch (e) { this.fail(e); await this.reload(); }
    },
    async onDetected(code) {
      this.camera = false;
      if (!this.scanTarget) return;
      const entry = this.scanTarget; this.scanTarget = null;
      try {
        const result = await packingPost('confirm', { line_id: entry.line.id, method: 'scan', code: String(code).trim(), quantity: 1 });
        await this.reload(); this.inform('کالا برای کارتن درست تأیید شد.');
        if (result.address_ready && !result.address_printed && !result.shortage && printerConnected()) await this.print(result, 'address');
      } catch (e) { this.fail(e); }
    },
    async manual(entry) {
      const note = window.prompt('دلیل ثبت دستی را بنویسید:'); if (!note) return;
      try { const result = await packingPost('confirm', { line_id: entry.line.id, method: 'manual', note, quantity: 1 }); await this.reload(); if (result.address_ready && !result.address_printed && !result.shortage && printerConnected()) await this.print(result, 'address'); } catch (e) { this.fail(e); }
    },
    async shortage(entry) {
      const note = window.prompt('دلیل کسری را بنویسید:'); if (!note) return;
      const qty = entry.line.quantity - entry.line.confirmed - entry.line.shortage;
      try { await packingPost('shortage', { line_id: entry.line.id, quantity: qty, note }); await this.reload(); } catch (e) { this.fail(e); }
    },
    async setLocation(entry) {
      const column = window.prompt('شماره ستون:', entry.line.column); if (!column) return;
      const row = window.prompt('شماره ردیف:', entry.line.row); if (!row) return;
      try { await packingPost('location', { product_id: entry.line.product_id, column, row }); await this.reload(); } catch (e) { this.fail(e); }
    },
    async split() {
      const items = Object.keys(this.splitAmounts).filter(id => Number(this.splitAmounts[id]) > 0)
        .map(id => ({ line_id: Number(id), quantity: Number(this.splitAmounts[id]) }));
      try { await packingPost('split', { carton_id: this.splitTarget.id, size: this.splitSize, items }); this.splitTarget = null; this.splitAmounts = {}; await this.reload(); this.inform('کارتن جدید ساخته شد. شناسه آن را چاپ کنید.'); } catch (e) { this.fail(e); }
    },
  },
};
</script>

<style scoped>
.packing-screen{max-width:900px;margin:auto;padding:12px;color:#1e293b;font-size:15px}.topbar,.summary>div{display:flex;justify-content:space-between;align-items:center;gap:10px}.topbar{background:#17324f;color:white;padding:16px;border-radius:14px}.card{background:white;border:1px solid #dbe3eb;border-radius:14px;padding:16px;margin:12px 0}.order-row,.plan,.carton,.pick-row{border-bottom:1px solid #e2e8f0;padding:12px 0}.carton.ready,.pick-row.done{background:#f0fdf4}.carton-title{font-size:20px;font-weight:bold}.carton-title small{font-size:13px}button,select,input{border:1px solid #a8bacb;border-radius:8px;padding:8px;margin:3px;min-height:42px}button{background:#f1f5f9}.primary{background:#125478;color:white}.actions,.pick-actions{display:flex;flex-wrap:wrap;gap:4px}.pick-row{display:flex;gap:10px;align-items:center}.pick-position{font-size:22px;font-weight:bold;min-width:70px;text-align:center}.product-image{width:55px;height:55px;object-fit:contain}.pick-info{flex:1;min-width:120px}.pick-info small{display:block}.notice,.warn{padding:9px;border-radius:9px}.good{background:#dcfce7}.bad,.warn{background:#fef3c7}.shortages{border-color:#f59e0b}.scanner-dialog,.modal{position:fixed;inset:0;z-index:100;background:#0f172ae8;padding:16px}.scanner-dialog>button{display:block}.scanner-dialog>input{width:100%}.modal{display:flex;align-items:center}.modal .card{width:100%;max-height:85vh;overflow:auto}.carton-plan{display:flex;justify-content:space-between}.sr-only{position:absolute;left:-9999px}@media(max-width:600px){.pick-row{flex-wrap:wrap}.pick-actions{width:100%}}
.printer-settings{padding:8px;background:white;border-radius:8px}.printer-settings label{display:block}.printer-settings input{width:min(100%,370px)}
</style>
